import {
  Avatar,
  Button,
  Card,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Alert } from '@material-ui/lab';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { messages } from 'containers/ChatAgent/messages';
import { AxAgentValues, ListedAxAgent } from 'containers/ChatAgent/types';
import { makeSelectLocale } from 'containers/LanguageProvider/selectors';
import { usePostHog } from 'hooks/usePostHog';

import BotSVG from '../images/bot.svg';
import { DeleteAgentConfirmationModal } from './modals/DeleteAgentConfirmationModal';
import { ExportAgentReportModal } from './modals/ExportAgentReportModal';
import { StatusChangeConfirmationModal } from './modals/StatusChangeConfirmationModal';
import { useAgentListStyles } from './styles';

interface AgentListProps {
  openProgressor: (values?: Partial<AxAgentValues>) => Promise<void>;
  loadSetupToken: () => Promise<string | null>;
  setRefreshToken: React.Dispatch<React.SetStateAction<boolean>>;
  axAgentValues: AxAgentValues;
  setAgents: React.Dispatch<React.SetStateAction<ListedAxAgent[]>>;
  agents: ListedAxAgent[];
  // Hire could not read every agent from fountain-ai.
  agentsIncomplete: boolean;
  accountSlug: string;
}

const GreenCircleIcon = () => (
  <svg
    width="6"
    height="7"
    viewBox="0 0 6 7"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="3" cy="3.5" r="3" fill="#009E52" />
  </svg>
);

const createdAtTime = (agent: ListedAxAgent) =>
  agent.createdAt ? new Date(agent.createdAt).getTime() : 0;

export const AgentList: React.FC<AgentListProps> = ({
  openProgressor,
  loadSetupToken,
  setRefreshToken,
  axAgentValues,
  setAgents,
  agents,
  agentsIncomplete,
  accountSlug,
}) => {
  const classes = useAgentListStyles();
  const locale = useSelector(makeSelectLocale());
  const posthog = usePostHog();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentAgent, setCurrentAgent] = useState<ListedAxAgent>();
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [openStatusChangeDialog, setOpenStatusChangeDialog] = useState(false);
  const [openExportReportModal, setOpenExportReportModal] = useState(false);

  const { feature_flags: featureFlags } = useSelector(makeSelectWhoami());
  const conversationReportEnabled = !!featureFlags?.['cai-conversation-report'];
  const { availableAgents } = axAgentValues;

  // The tab opens inside the click so no popup blocker stops it; it navigates
  // once the token call has written the widget URL.
  const openWidget = async (agentId: string) => {
    const popup = window.open('', '_blank');
    const token = await loadSetupToken();
    if (!token) {
      popup?.close();
      return;
    }
    const chatAgentWidgetUrl = localStorage.getItem('chatAgentWidgetUrl');
    const widgetUrl = `${String(chatAgentWidgetUrl)}/ax-agent/${agentId}/apply/chat?show=true`;
    if (popup) {
      popup.opener = null;
      popup.location.href = widgetUrl;
    } else {
      window.open(widgetUrl, '_blank');
    }
  };

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    agent: ListedAxAgent,
  ) => {
    setAnchorEl(event.currentTarget);
    setCurrentAgent(agent);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (agent: ListedAxAgent, hasActiveAgent: boolean) => {
    handleClose();
    void openProgressor({
      axAgentId: agent.uuid,
      knowledgeBaseUuid: agent.knowledgeBaseUuid ?? undefined,
      hasActiveAgent,
    });
  };

  const handleDelete = (agent: ListedAxAgent) => {
    setCurrentAgent(agent);
    setOpenDeleteConfirmDialog(true);
  };

  const handleUpdateStatus = (agent: ListedAxAgent) => {
    setCurrentAgent(agent);
    setOpenStatusChangeDialog(true);
  };

  const handleExportReport = (agent: ListedAxAgent) => {
    posthog.capture('candidate_ai_agent:export_report_click', {
      agent_id: agent.uuid,
    });
    setCurrentAgent(agent);
    setOpenExportReportModal(true);
  };

  const activeAgents = agents
    .filter(agent => !agent.isDeleted)
    .sort((a, b) => createdAtTime(b) - createdAtTime(a));

  const renderStatus = (status: ListedAxAgent['status']) => {
    switch (status) {
      case 'active':
        return <GreenCircleIcon />;
      case 'draft':
        return <div className={classes.draftLabel}>Draft</div>;
      default:
        return null;
    }
  };

  return (
    <Grid className={classes.container}>
      <Grid className={classes.header}>
        <Typography variant="h2">
          <FormattedMessage {...messages.candidateAiAgent} />
        </Typography>
        <span>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => void openProgressor()}
            className={classes.button}
          >
            <FormattedMessage {...messages.setupAiAgent} />
          </Button>
        </span>
      </Grid>
      {agentsIncomplete && (
        <Alert severity="warning">
          <FormattedMessage {...messages.agentListIncomplete} />
        </Alert>
      )}
      {activeAgents.map(agent => {
        const matchingAvailableAgent = availableAgents.find(
          availableAgent => availableAgent.agent_id === agent.uuid,
        );
        return (
          <Card key={agent.uuid} className={classes.card}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Avatar src={BotSVG} className={classes.avatar} />
              <div className={classes.details}>
                <div className={classes.cardTitle}>
                  <div className={classes.agentName}>
                    {renderStatus(agent.status)}
                    <Typography variant="h3">{agent.name}</Typography>
                  </div>
                  <Typography variant="body2">
                    {matchingAvailableAgent?.brand_name}
                  </Typography>
                </div>
                {agent.updatedAt && (
                  <Typography variant="body2" color="textSecondary">
                    {agent.lastModifiedBy ? 'Modified on' : 'Created on'}{' '}
                    {new Date(agent.updatedAt).toLocaleString(locale, {
                      dateStyle: 'medium',
                    })}{' '}
                    {agent.modifiedByName ? `by ${agent.modifiedByName}` : ''}
                  </Typography>
                )}
              </div>
            </div>
            <div className={classes.actions}>
              <Button
                variant="text"
                color="primary"
                startIcon={<OpenInNewIcon />}
                className={classes.agentButton}
                onClick={() => void openWidget(agent.uuid)}
              >
                <FormattedMessage {...messages.testAiAgent} />
              </Button>
              <div>
                <IconButton
                  onClick={event => handleClick(event, agent)}
                  className={
                    anchorEl && currentAgent?.uuid === agent.uuid
                      ? classes.iconButtonHighlighted
                      : classes.iconButton
                  }
                >
                  <MoreHorizIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && currentAgent?.uuid === agent.uuid}
                  onClose={handleClose}
                  classes={{ paper: classes.customMenu }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleEdit(
                        agent,
                        matchingAvailableAgent?.has_active_agent as boolean,
                      );
                    }}
                    className={classes.menuItem}
                  >
                    Edit
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleUpdateStatus(agent);
                      handleClose();
                    }}
                    className={classes.menuItem}
                    disabled={matchingAvailableAgent?.has_active_agent}
                  >
                    {agent.status === 'draft' ? 'Publish' : 'Unpublish'}
                  </MenuItem>
                  {conversationReportEnabled && agent.status !== 'draft' && (
                    <MenuItem
                      onClick={() => {
                        handleExportReport(agent);
                        handleClose();
                      }}
                      className={classes.menuItem}
                    >
                      <FormattedMessage {...messages.exportReport} />
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      handleDelete(agent);
                    }}
                    className={classes.menuItem}
                  >
                    Delete
                  </MenuItem>
                </Menu>
              </div>
            </div>
          </Card>
        );
      })}
      <DeleteAgentConfirmationModal
        handleClose={handleClose}
        currentAgentId={currentAgent?.uuid}
        openConfirmDialog={openDeleteConfirmDialog}
        setOpenConfirmDialog={setOpenDeleteConfirmDialog}
        setRefreshToken={setRefreshToken}
        setAgents={setAgents}
        accountSlug={accountSlug}
      />

      <StatusChangeConfirmationModal
        handleClose={handleClose}
        agent={currentAgent}
        openConfirmDialog={openStatusChangeDialog}
        setOpenConfirmDialog={setOpenStatusChangeDialog}
        setRefreshToken={setRefreshToken}
        accountSlug={accountSlug}
        loadSetupToken={loadSetupToken}
      />

      {openExportReportModal && (
        <ExportAgentReportModal
          onClose={() => {
            setOpenExportReportModal(false);
            handleClose();
          }}
          agent={currentAgent}
          accountSlug={accountSlug}
        />
      )}
    </Grid>
  );
};
